import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckSquare, Gift, DollarSign, Calendar, Users, ExternalLink, Share2, Copy, Download } from 'react-feather';
import { useCouple } from '../../contexts/CoupleContext';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/layouts/AdminLayout';
import RegistryService from '../../services/registry.service';
import TodoService from '../../services/todo.service';
import PaymentService from '../../services/payment.service';
import { Gift as GiftType, Todo, ContributionSummary } from '../../types';
import { formatPriceSimple, SupportedCurrency } from '../../utils/currency';
import QRCode from 'react-qr-code'; // You'll need to install this: npm install react-qr-code
import { toast } from 'react-toastify';

interface DashboardData {
  gifts: {
    total: number;
    purchased: number;
    reserved: number;
  };
  todos: {
    total: number;
    completed: number;
  };
  contributions: ContributionSummary;
  recentGifts: GiftType[];
  upcomingTodos: Todo[];
}

const Dashboard: React.FC = () => {
  const { currentCouple } = useCouple();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [paymentLink, setPaymentLink] = useState<string>("");
  const [weddingLink, setWeddingLink] = useState<string>("");
  const [showQrCode, setShowQrCode] = useState<boolean>(false);
  const [qrCodeValue, setQrCodeValue] = useState<string>("");
  const [qrCodeTitle, setQrCodeTitle] = useState<string>("");
  
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    gifts: {
      total: 0,
      purchased: 0,
      reserved: 0
    },
    todos: {
      total: 0,
      completed: 0
    },
    contributions: {
      totalAmount: 0,
      totalCount: 0
    },
    recentGifts: [],
    upcomingTodos: []
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!currentCouple) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch gift registry data
        const gifts = await RegistryService.getCoupleGifts(currentCouple._id);
        
        // Fetch todos
        const todos = await TodoService.getCoupleTodos(currentCouple._id);
        
        // Fetch contributions
        const contributions = await PaymentService.getContributionSummary(currentCouple._id);
        
        // Process gifts
        const purchasedGifts = gifts.filter(gift => gift.status === 'purchased');
        const reservedGifts = gifts.filter(gift => gift.status === 'reserved');
        const recentGifts = [...purchasedGifts, ...reservedGifts]
          .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
          .slice(0, 5);
        
        // Process todos
        const completedTodos = todos.filter(todo => todo.completed);
        const upcomingTodos = todos
          .filter(todo => !todo.completed)
          .sort((a, b) => {
            if (!a.dueDate) return 1;
            if (!b.dueDate) return -1;
            return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
          })
          .slice(0, 5);
        
        setDashboardData({
          gifts: {
            total: gifts.length,
            purchased: purchasedGifts.length,
            reserved: reservedGifts.length
          },
          todos: {
            total: todos.length,
            completed: completedTodos.length
          },
          contributions: contributions,
          recentGifts,
          upcomingTodos
        });

        // Generate links
        generateLinks();
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error('Failed to load dashboard data');
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [currentCouple, currentUser]);

  const generateLinks = () => {
    if (!currentCouple) return;
    
    // Create a payment link with the couple's information
    const baseUrl = window.location.origin;
    const contributionLink = `${baseUrl}/wedding/${currentCouple.customUrl}/contribute`;
    const weddingPageLink = `${baseUrl}/wedding/${currentCouple.customUrl}`;
    
    setPaymentLink(contributionLink);
    setWeddingLink(weddingPageLink);
  };

  const copyToClipboard = (text: string, message: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        toast.success(message);
      })
      .catch((error) => {
        console.error('Error copying to clipboard:', error);
        toast.error('Failed to copy to clipboard');
      });
  };

  const shareWeddingPage = () => {
    if (!currentCouple) return;
    
    if (navigator.share) {
      navigator.share({
        title: `${currentCouple.names} Wedding`,
        text: `Join us in celebrating ${currentCouple.names} wedding!`,
        url: weddingLink,
      })
        .then(() => {
          toast.success('Wedding page shared successfully');
        })
        .catch((error) => {
          console.log('Error sharing:', error);
          if (error.name !== 'AbortError') {
            toast.error('Failed to share wedding page');
          }
        });
    } else {
      copyToClipboard(weddingLink, 'Wedding page link copied to clipboard!');
    }
  };

  const sharePaymentLink = () => {
    if (!paymentLink) return;
    
    if (navigator.share) {
      navigator.share({
        title: `Contribute to ${currentCouple?.names} Wedding`,
        text: `Contribute to ${currentCouple?.names} wedding fund using this link:`,
        url: paymentLink,
      })
        .then(() => {
          toast.success('Payment link shared successfully');
        })
        .catch((error) => {
          console.log('Error sharing payment link:', error);
          if (error.name !== 'AbortError') {
            toast.error('Failed to share payment link');
          }
        });
    } else {
      copyToClipboard(paymentLink, 'Payment link copied to clipboard!');
    }
  };

  const showWeddingQrCode = () => {
    setQrCodeValue(weddingLink);
    setQrCodeTitle(`${currentCouple?.names} Wedding Page`);
    setShowQrCode(true);
  };

  const showPaymentQrCode = () => {
    setQrCodeValue(paymentLink);
    setQrCodeTitle(`Contribute to ${currentCouple?.names} Wedding`);
    setShowQrCode(true);
  };

  const downloadQrCode = () => {
    const svg = document.getElementById('qr-code');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');
      
      // Download the file
      const downloadLink = document.createElement('a');
      downloadLink.download = `${qrCodeTitle.replace(/\s+/g, '-').toLowerCase()}-qr-code.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
      
      toast.success('QR Code downloaded successfully');
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  if (!currentCouple) {
    return (
      <AdminLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-10">
          <div className="text-center py-16">
            <h3 className="text-2xl font-medium text-gray-900">Welcome to WedPlanner!</h3>
            <p className="mt-4 text-gray-500">Let's start by creating your wedding profile.</p>
            <div className="mt-6">
              <Link
                to="/admin/create-couple"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
              >
                Create Wedding Profile
              </Link>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-10">
          <div className="text-center py-16">
            <p className="text-gray-500">Loading dashboard...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const getFundingPercentage = (): number => {
    if (!currentCouple.fundingGoal || currentCouple.fundingGoal <= 0) {
      return 0;
    }
    
    const percentage = (dashboardData.contributions.totalAmount / currentCouple.fundingGoal) * 100;
    return Math.min(percentage, 100);
  };

  const getTodoPercentage = (): number => {
    if (dashboardData.todos.total === 0) {
      return 0;
    }
    
    return (dashboardData.todos.completed / dashboardData.todos.total) * 100;
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-10">
        <div className="md:flex md:items-center md:justify-between md:space-x-5">
          <div className="flex items-start space-x-5">
            <div className="flex-shrink-0">
              <div className="relative">
                <div className="h-16 w-16 rounded-full bg-pink-100 flex items-center justify-center">
                  <span className="text-2xl font-bold text-pink-600">
                    {currentCouple.names.split(' ')[0]?.charAt(0) || ''}
                    {currentCouple.names.split(' ').pop()?.charAt(0) || ''}
                  </span>
                </div>
              </div>
            </div>
            <div className="pt-1.5">
              <h1 className="text-2xl font-bold text-gray-900">{currentCouple.names}</h1>
              <p className="text-sm font-medium text-gray-500">
                {new Date(currentCouple.weddingDate).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
                {currentCouple.weddingLocation && ` • ${currentCouple.weddingLocation}`}
              </p>
            </div>
          </div>
          <div className="mt-6 flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-3 sm:space-y-0 sm:space-x-reverse md:mt-0 md:flex-row md:space-x-3">
            <Link
              to={`/wedding/${currentCouple.customUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              View Wedding Page
            </Link>
            <Link
              to="/admin/couple-settings"
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
            >
              Edit Profile
            </Link>
          </div>
        </div>

        {/* Share Buttons */}
        <div className="mt-6 bg-white shadow rounded-lg overflow-hidden">
          <div className="p-5">
            <h3 className="text-lg font-medium text-gray-900">Share Your Wedding</h3>
            <p className="mt-1 text-sm text-gray-500">
              Share your wedding page and payment link with friends and family
            </p>
            
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Wedding Page Sharing */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-medium text-gray-900">Wedding Page</h4>
                <div className="mt-3 text-sm text-gray-500 break-all">{weddingLink}</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button 
                    onClick={shareWeddingPage} 
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    <Share2 className="mr-1.5 h-4 w-4" />
                    Share
                  </button>
                  <button 
                    onClick={() => copyToClipboard(weddingLink, 'Wedding link copied!')} 
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Copy className="mr-1.5 h-4 w-4" />
                    Copy
                  </button>
                  <button 
                    onClick={showWeddingQrCode} 
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <span className="mr-1.5">QR</span>
                    Show QR Code
                  </button>
                </div>
              </div>
              
              {/* Payment Link Sharing */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-medium text-gray-900">Payment Link</h4>
                <div className="mt-3 text-sm text-gray-500 break-all">{paymentLink}</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button 
                    onClick={sharePaymentLink} 
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded text-white bg-green-600 hover:bg-green-700"
                  >
                    <Share2 className="mr-1.5 h-4 w-4" />
                    Share
                  </button>
                  <button 
                    onClick={() => copyToClipboard(paymentLink, 'Payment link copied!')} 
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Copy className="mr-1.5 h-4 w-4" />
                    Copy
                  </button>
                  <button 
                    onClick={showPaymentQrCode} 
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <span className="mr-1.5">QR</span>
                    Show QR Code
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* QR Code Modal */}
        {showQrCode && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{qrCodeTitle}</h3>
              
              <div className="flex justify-center bg-white p-4 rounded-lg mb-4">
                <QRCode
                  id="qr-code"
                  value={qrCodeValue}
                  size={200}
                  level="H"
                  bgColor="#FFFFFF"
                  fgColor="#000000"
                />
              </div>
              
              <div className="text-sm text-gray-500 text-center mb-4 break-all">
                {qrCodeValue}
              </div>
              
              <div className="flex justify-center space-x-3">
                <button
                  onClick={downloadQrCode}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <Download className="mr-1.5 h-4 w-4" />
                  Download
                </button>
                <button
                  onClick={() => setShowQrCode(false)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {/* Wedding Fund Card */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DollarSign className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Wedding Fund</dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {formatPriceSimple(
                          dashboardData.contributions.totalAmount,
                          (dashboardData.contributions.currency as SupportedCurrency) || 'NGN'
                        )}
                      </div>
                      {currentCouple?.fundingGoal && currentCouple.fundingGoal > 0 && (
                        <div className="ml-2 text-sm text-gray-500">
                          of {formatPriceSimple(
                            currentCouple.fundingGoal,
                            (dashboardData.contributions.currency as SupportedCurrency) || 'NGN'
                          )}
                        </div>
                      )}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-5 py-3">
              <div className="text-sm">
                <div className="mb-1 flex justify-between items-center">
                  <span className="text-gray-500">Progress</span>
                  <span className="text-gray-900">
                    {Math.round(getFundingPercentage())}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-green-500 h-2.5 rounded-full" 
                    style={{ width: `${getFundingPercentage()}%` }}
                  ></div>
                </div>
              </div>
              <div className="mt-2">
                <Link
                  to="/admin/wedding-fund"
                  className="text-sm font-medium text-pink-600 hover:text-pink-500"
                >
                  View all contributions
                </Link>
              </div>
            </div>
          </div>

          {/* To-Do List Card */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckSquare className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Wedding To-Do</dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {dashboardData.todos.completed}
                      </div>
                      <div className="ml-2 text-sm text-gray-500">
                        of {dashboardData.todos.total} completed
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-5 py-3">
              <div className="text-sm">
                <div className="mb-1 flex justify-between items-center">
                  <span className="text-gray-500">Progress</span>
                  <span className="text-gray-900">
                    {Math.round(getTodoPercentage())}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-blue-500 h-2.5 rounded-full" 
                    style={{ width: `${getTodoPercentage()}%` }}
                  ></div>
                </div>
              </div>
              <div className="mt-2">
                <Link
                  to="/admin/todos"
                  className="text-sm font-medium text-pink-600 hover:text-pink-500"
                >
                  View all tasks
                </Link>
              </div>
            </div>
          </div>

          {/* Gift Registry Card */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Gift className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Gift Registry</dt>
                    <dd className="flex items-center">
                      <div className="text-2xl font-semibold text-gray-900">
                        {dashboardData.gifts.purchased}
                      </div>
                      <div className="ml-2 text-sm text-gray-500">
                        purchased, {dashboardData.gifts.reserved} reserved
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-5 py-3">
              <div className="text-sm">
                <div className="mb-1 flex items-center">
                  <span className="text-gray-500">Total Gifts: </span>
                  <span className="ml-1 text-gray-900">{dashboardData.gifts.total}</span>
                </div>
              </div>
              <div className="mt-2">
                <Link
                  to="/admin/registry"
                  className="text-sm font-medium text-pink-600 hover:text-pink-500"
                >
                  View gift registry
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Recent Gifts */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Recent Gifts</h3>
            </div>
            {dashboardData.recentGifts.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {dashboardData.recentGifts.map((gift) => (
                  <li key={gift._id} className="px-5 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Gift className="h-5 w-5 text-gray-400" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{gift.name}</div>
                          <div className="text-sm text-gray-500">
                            From: {gift.contributorName || 'Anonymous'}
                          </div>
                        </div>
                      </div>
                      <div>
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            gift.status === 'purchased'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {gift.status === 'purchased' ? 'Purchased' : 'Reserved'}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-5 py-4 text-center text-gray-500">
                No gifts received yet
              </div>
            )}
            {dashboardData.recentGifts.length > 0 && (
              <div className="px-5 py-4 border-t border-gray-200 text-right">
                <Link
                  to="/admin/registry"
                  className="text-sm font-medium text-pink-600 hover:text-pink-500"
                >
                  View all gifts
                </Link>
              </div>
            )}
          </div>

          {/* Upcoming Tasks */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Upcoming Tasks</h3>
            </div>
            {dashboardData.upcomingTodos.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {dashboardData.upcomingTodos.map((todo) => (
                  <li key={todo._id} className="px-5 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                            <CheckSquare className="h-5 w-5 text-gray-400" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{todo.title}</div>
                          <div className="text-sm text-gray-500">
                            {todo.dueDate 
                              ? `Due: ${new Date(todo.dueDate).toLocaleDateString()}`
                              : 'No due date'}
                          </div>
                        </div>
                      </div>
                      <div>
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            todo.priority === 'high'
                              ? 'bg-red-100 text-red-800'
                              : todo.priority === 'medium'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-green-100 text-green-800'
                          }`}
                        >
                          {todo.priority}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-5 py-4 text-center text-gray-500">
                No upcoming tasks
              </div>
            )}
            {dashboardData.upcomingTodos.length > 0 && (
              <div className="px-5 py-4 border-t border-gray-200 text-right">
                <Link
                  to="/admin/todos"
                  className="text-sm font-medium text-pink-600 hover:text-pink-500"
                >
                  View all tasks
                </Link>
              </div>
            )}
            </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;